import { NewsletterModal } from '@fontbold/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { cookieConsent, onCategoryDenied } from '#/lib/consent';
import { NEWSLETTER_MODAL_TRANSLATIONS } from '#/lib/translations';
import { subscribeToNewsletter } from '#/routes/-mutations';

const EVENT = 'cf:open-newsletter';
const COOKIE_NAME = 'newsletter_dismissed';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const AUTO_OPEN_DELAY = 6000;

export function openNewsletter(): void {
  window.dispatchEvent(new CustomEvent(EVENT));
}

function isDismissed(): boolean {
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith(`${COOKIE_NAME}=`));
}

function setDismissedCookie(): void {
  if (!cookieConsent.isAllowed('functional')) return;
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not supported in Safari/Firefox yet
  document.cookie = `${COOKIE_NAME}=1; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
}

function clearDismissedCookie(): void {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not supported in Safari/Firefox yet
  document.cookie = `${COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
}

onCategoryDenied('functional', clearDismissedCookie);

export function AppNewsletter({
  spaceId,
  logo,
}: {
  spaceId: string;
  logo?: string;
}) {
  const [open, setOpen] = useState(false);

  const subscribe = useMutation({
    mutationFn: (email: string) => subscribeToNewsletter(spaceId, email),
  });

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(EVENT, onOpen);

    let timeout: number | undefined;
    if (!isDismissed()) {
      timeout = window.setTimeout(() => setOpen(true), AUTO_OPEN_DELAY);
    }

    return () => {
      window.removeEventListener(EVENT, onOpen);
      if (timeout) window.clearTimeout(timeout);
    };
  }, []);

  const close = () => {
    setDismissedCookie();
    setOpen(false);
  };

  return (
    <NewsletterModal
      open={open}
      logo={logo}
      translations={NEWSLETTER_MODAL_TRANSLATIONS}
      socialProof={['4.200+ lezers', 'Altijd uitschrijven']}
      onClose={close}
      onSubmit={async (email) => {
        await subscribe.mutateAsync(email);
        setDismissedCookie();
      }}
    />
  );
}
